import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';

/**
 * Desserialização defensiva do `payload`. Já tivemos bug onde:
 *   1. Backend devolvia payload como STRING (Drizzle não parseava JSON).
 *   2. Frontend fazia `...payload` na string → objeto char-indexed
 *      (`{"0":"{", "1":"\""...}`).
 *   3. Save subsequente persistia o lixo permanentemente.
 *
 * Backend já foi corrigido pra desserializar, mas mantemos defesa aqui pra
 * sobreviver a regressões. Cobre 3 casos:
 *   - string JSON → parse
 *   - objeto char-indexed → reconstrói string + parse
 *   - objeto válido → devolve direto
 */
function safePayloadObject(raw) {
    if (raw == null) return {};
    if (typeof raw === 'string') {
        try {
            return safePayloadObject(JSON.parse(raw));
        } catch {
            return {};
        }
    }
    if (typeof raw === 'object') {
        const keys = Object.keys(raw);
        const looksCharIndexed =
            keys.length > 0 &&
            keys.every((k, i) => k === String(i)) &&
            keys.every((k) => typeof raw[k] === 'string' && raw[k].length === 1);
        if (looksCharIndexed) {
            const reconstructed = keys.map((k) => raw[k]).join('');
            try {
                return JSON.parse(reconstructed);
            } catch {
                return {};
            }
        }
        return raw;
    }
    return {};
}

/** Achata a row do backend (metadados + payload) num único objeto plano que o
 *  resto da página usa (preset.columns, preset.filters, etc). */
function flattenPreset(p) {
    return {
        id: p.id,
        name: p.name,
        category: p.category,
        createdBy: p.created_by || null,
        createdAt: p.created_at,
        updatedAt: p.updated_at,
        ...safePayloadObject(p.payload),
    };
}

/**
 * Presets vivem no backend (`/api/v2/presets`). Esse hook é o front client —
 * carrega no mount, expõe ações async + estado de loading.
 *
 *   - `allPresets`: lista completa visível pro user atual (filtrada server-side
 *     por organização + categoria Pessoal).
 *   - `loading` / `error`: estado da carga inicial e mutações.
 *   - `createPreset / updatePreset / deletePreset`: ações async; atualizam o
 *     estado local otimistamente e propagam o resultado do backend.
 *   - `reload`: força refetch (útil após operação externa).
 *
 * Categoria 'Pessoal' = só dono vê e edita. Outras categorias = todos da org.
 * Não há mais distinção entre "sistema" e "custom" — todo preset é editável,
 * mesmo os criados pelo seed (`created_by = null` → categoria não-Pessoal).
 */
export function useCustomPresets() {
    const [presets, setPresets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const inFlight = useRef(false);

    const load = useCallback(async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        setLoading(true);
        setError(null);
        try {
            const resp = await api.listPresets();
            // Backend pode devolver { results } ou array direto (handleRoute desempacota).
            const list = Array.isArray(resp) ? resp : resp?.results || [];
            setPresets(list.map(flattenPreset));
        } catch (err) {
            setError({
                message: err?.message || 'Não foi possível carregar os presets.',
                code: err?.code || 'OPS26_PRESETS_LOAD',
            });
            console.error('[operacoes/presets/load]', err?.code || 'OPS26_PRESETS_LOAD', err);
        } finally {
            setLoading(false);
            inFlight.current = false;
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const createPreset = useCallback(async (data) => {
        const { name, category, id: _id, createdBy: _cb, createdAt: _ca, updatedAt: _ua, ...rest } = data || {};
        const payload = { ...rest };
        const created = await api.createPreset({ name, category, payload });
        const next = flattenPreset(created);
        setPresets((prev) => [...prev, next]);
        return next;
    }, []);

    const updatePreset = useCallback(async (id, updates) => {
        const patch = {};
        if (updates.name !== undefined) patch.name = updates.name;
        if (updates.category !== undefined) patch.category = updates.category;
        // Tudo que não é metadado vai pro payload (columns, filters, sort, identifier, columnViews...).
        const META_KEYS = ['id', 'name', 'category', 'createdBy', 'createdAt', 'updatedAt'];
        const { name: _n, category: _c, id: _i, createdBy: _b, createdAt: _ca, updatedAt: _ua, ...payloadPatch } =
            updates;
        if (Object.keys(payloadPatch).length > 0) {
            // Pega o existing payload da lista atual. Importante: o `existing` veio
            // do flattenPreset, que JÁ blindou contra string/char-indexed. Mas se
            // o callsite passou um draft com payload corrompido, payloadPatch herda
            // o lixo — `safePayloadObject` defende contra isso.
            const existing = presets.find((p) => p.id === id);
            const existingPayload = existing
                ? Object.fromEntries(
                      Object.entries(existing).filter(([k]) => !META_KEYS.includes(k)),
                  )
                : {};
            patch.payload = safePayloadObject({ ...existingPayload, ...payloadPatch });
        }
        const updated = await api.updatePreset(id, patch);
        const next = flattenPreset(updated);
        setPresets((prev) => prev.map((p) => (p.id === id ? next : p)));
        return next;
    }, [presets]);

    const deletePreset = useCallback(async (id) => {
        await api.deletePreset(id);
        setPresets((prev) => prev.filter((p) => p.id !== id));
    }, []);

    return {
        allPresets: presets,
        loading,
        error,
        reload: load,
        createPreset,
        updatePreset,
        deletePreset,
    };
}
